import * as THREE from 'three';

function nameHintsLiquid(name: string): boolean {
  return /liquid|cognac|wine|fill|fluid|content|eau|spirit/i.test(name);
}

function colorLuminance(c: THREE.Color): number {
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
}

function colorSaturation(c: THREE.Color): number {
  return Math.max(c.r, c.g, c.b) - Math.min(c.r, c.g, c.b);
}

function isNearWhite(c: THREE.Color, eps = 0.02): boolean {
  return c.r >= 1 - eps && c.g >= 1 - eps && c.b >= 1 - eps;
}

/**
 * Three.js Beer–Lambert uses `pow(attenuationColor, thickness / attenuationDistance)`.
 * Distance ≤ 0 causes a divide-by-zero (full absorb / black). Always use Infinity or > 0.
 */
export function sanitizeAttenuationDistance(distance: number | undefined | null): number {
  if (distance == null || !Number.isFinite(distance) || distance <= 0) return Infinity;
  return distance;
}

export interface TransmissionAttenuationOptions {
  meshName?: string;
  parentName?: string;
  /** Explicit Beer–Lambert tint. White / unset → derive from base color when auto. */
  attenuationColor?: string;
  /**
   * Average absorption distance in world units.
   * ≤ 0 / unset = auto (derive when liquid / colored) or Infinity (clear).
   * Never written as 0 onto the material.
   */
  attenuationDistance?: number;
  /** Clear glass shell — weak white attenuation only. */
  clearGlass?: boolean;
}

/**
 * Beer–Lambert absorption for MeshPhysicalMaterial transmission.
 * Without a finite attenuationDistance, refracted background reads at full contrast.
 */
export function applyTransmissionAttenuation(
  mat: THREE.MeshPhysicalMaterial,
  options?: TransmissionAttenuationOptions,
): void {
  if ((mat.transmission ?? 0) <= 0.001) return;

  const meshName = options?.meshName ?? '';
  const parentName = options?.parentName ?? '';
  const isLiquid = nameHintsLiquid(meshName) || nameHintsLiquid(parentName);

  const explicitDistRaw = options?.attenuationDistance;
  const hasExplicitDist =
    explicitDistRaw != null && Number.isFinite(explicitDistRaw) && explicitDistRaw > 0;

  let absorbTint: THREE.Color | null = null;
  if (options?.attenuationColor) {
    const t = new THREE.Color();
    try {
      t.set(options.attenuationColor);
      if (!isNearWhite(t)) absorbTint = t;
    } catch {
      /* ignore bad hex */
    }
  }

  // Explicit distance wins — clamp so we never write 0 (NaN path in the shader).
  if (hasExplicitDist) {
    if (absorbTint) mat.attenuationColor.copy(absorbTint);
    else if (isNearWhite(mat.attenuationColor) && !isNearWhite(mat.color)) {
      mat.attenuationColor.copy(mat.color);
    }
    mat.attenuationDistance = explicitDistRaw!;
    mat.needsUpdate = true;
    return;
  }

  if (options?.clearGlass) {
    mat.attenuationColor.set(0xffffff);
    mat.attenuationDistance = Infinity;
    mat.needsUpdate = true;
    return;
  }

  const base = mat.color;
  const lum = colorLuminance(base);
  const sat = colorSaturation(base);
  const coloredBase = lum < 0.72 && sat > 0.06;
  const wantsAbsorb = !!absorbTint || isLiquid || coloredBase;

  if (!wantsAbsorb) {
    // Clear glass / no tint: no absorption. Critical: never leave distance at 0.
    mat.attenuationColor.set(0xffffff);
    mat.attenuationDistance = Infinity;
    mat.needsUpdate = true;
    return;
  }

  // Auto: tint from Absorb Color when set, else from base color.
  if (absorbTint) mat.attenuationColor.copy(absorbTint);
  else mat.attenuationColor.copy(base);

  const thickness = Math.max(mat.thickness ?? 0.1, 0.05);
  const tintLum = colorLuminance(mat.attenuationColor);
  const tintSat = colorSaturation(mat.attenuationColor);
  // Darker / more saturated → shorter mean free path (stronger absorb).
  const absorbStrength = THREE.MathUtils.clamp(1.05 - tintLum * 0.85 - tintSat * 0.15, 0.18, 1);
  // Scale so typical Thickness 0.1–0.3 lands in a useful Absorb Dist range (~0.08–0.5).
  mat.attenuationDistance = Math.max(0.06, thickness * (2.8 - absorbStrength * 1.4));
  mat.needsUpdate = true;
}
